import {
  alpha,
  createTheme,
  darken,
  lighten,
  PaletteMode,
} from "@mui/material";
import type { Shadows, ThemeOptions } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";

// DESIGN.md mandates "no shadows anywhere". MUI ships a 25-slot shadows array
// (index 0 = "none", 1-24 = progressively deeper Material drop-shadows) that
// every `<Paper elevation={N}>` reads from — including internals like Dialog,
// Menu, and Popover. Overriding MuiPaper.elevation1 alone isn't enough because
// any other elevation still renders. Flatten the whole array so no consumer
// can opt back into a shadow via elevation.
const NO_SHADOWS = Array(25).fill("none") as unknown as Shadows;

// DESIGN.md palette tokens — source of truth for the design system.
// These are added additively in PR 1 of the design system migration.
// Consumers should reference theme.palette.{tokenName} instead of hardcoding hex values.
export const designTokens = {
  cinnabar: "#D03A14",
  brown: "#432D2B",
  nearBlack: "#110A09",
  momentum: "#1B6B4A",
  // DESIGN.md → Progress Indicators (low-emphasis). `momentum` composited at
  // ~70% over white — a muted sage for resting, low-emphasis progress fills
  // (checklist segment bars, sub-progress mini-bars) so a quiet dense surface
  // doesn't read as aggressive full-strength green. Completed-state checkmarks
  // still use full `momentum`. Blessed 2026-09-09; see DESIGN-TODOS.md.
  momentumMuted: "#5F9780",
  amber: "#D97706",
  inactiveGray: "#D1D5DB",
  borderToken: "#E5E7EB",
  // `mutedText`, not `textSecondary`, to avoid colliding with MUI's
  // `palette.text.secondary` (which `<Typography color="textSecondary">`
  // resolves to via the deprecated-color shim).
  mutedText: "#6B7280",
  // Input label + focus border on the student theme. Deliberately NOT cinnabar
  // because a cinnabar focus border reads as an error state next to the new
  // error red (#B91C1C). See DESIGN.md → Inputs for the full rationale.
  inputChrome: "#5C5450",
  // DESIGN.md → Color → the one sanctioned warm-neutral. `surfaceMuted` is a
  // subtle recessed surface (FAQ cards, neutral buttons, controls); `borderMuted`
  // is its hairline. Light-surface only — it has no near-black equivalent (the
  // dark theme uses a translucent white lift instead; see DESIGN.md).
  surfaceMuted: "#FAF8F8",
  borderMuted: "#E8E0DF",
  errorLight: "#FEF2F2",
  warningLight: "#FFFBEB",
  successLight: "#F0FDF4",
  infoLight: "#EFF6FF",
} as const;

// DESIGN.md → Color → Celebration / Delight palette. An expressive, off-core
// palette sanctioned ONLY for full-screen celebratory/delightful moments —
// "Wrapped"-style achievement wizards (the CDL Wrapped / Job Search Success
// Plan slides are the reference). It is deliberately reachable only through the
// `celebration` namespace (`theme.palette.celebration.*`) so the name itself
// carries the restriction: never reach for these on a routine card, section,
// page background, or admin surface — new uses go through /design-critic.
//
// These are FIXED brand hues, NOT theme-derived: they stay identical on
// `studentTheme` and `adminTheme` and must never track `primary`.
//
// `accent` intentionally resolves to the brand `cinnabar` — the legacy CDL
// Wrapped starburst red (#E34234) was drift, reconciled here to the one brand
// red. `surface` is the deep navy full-bleed ground (paired with white text);
// `blue`/`skyBlue` are the sanctioned celebratory blues (the one place blue is
// on-brand). `cream`/`paleNavy`/`offWhite` are the light celebratory grounds.
export const celebrationTokens = {
  surface: "#002D5A",
  blue: "#4B75B6",
  skyBlue: "#DCE9FF",
  accent: designTokens.cinnabar,
  accentHover: darken(designTokens.cinnabar, 0.1),
  cream: "#F7F1EA",
  paleNavy: "#F0F4FA",
  offWhite: "#FAFAF9",
} as const;

export type CelebrationPalette = typeof celebrationTokens;

// 8% wash of a color — the standardized interactive-emphasis fill
// (hover / selected / active). See DESIGN.md → Buttons → States.
const wash = (color: string) => alpha(color, 0.08);

declare module "@mui/material/styles" {
  // Required on `Palette` (the resolved theme) so consumers can read
  // `theme.palette.nearBlack` without a fallback — both `studentTheme` and
  // `adminTheme` are built from `baseThemeOptions`, which sets every token.
  // `PaletteOptions` (the input shape passed to `createTheme`) stays partial
  // so unrelated `createTheme` calls (e.g. `darkTheme`) still typecheck.
  interface Palette {
    cinnabar: string;
    brown: string;
    nearBlack: string;
    momentum: string;
    momentumMuted: string;
    amber: string;
    inactiveGray: string;
    borderToken: string;
    mutedText: string;
    inputChrome: string;
    surfaceMuted: string;
    borderMuted: string;
    // Celebration / Delight palette — expressive, off-core colors sanctioned
    // only for full-screen celebratory moments. See `celebrationTokens`.
    celebration: CelebrationPalette;
    errorLight: string;
    warningLight: string;
    successLight: string;
    infoLight: string;
    // 8% wash of the active theme's primary — the standardized fill for
    // interactive emphasis (hover / selected / active). Derived from each
    // theme's primary.main just after createTheme; see DESIGN.md → Buttons.
    selectionWash: string;
  }
  interface PaletteOptions {
    cinnabar?: string;
    brown?: string;
    nearBlack?: string;
    momentum?: string;
    momentumMuted?: string;
    amber?: string;
    inactiveGray?: string;
    borderToken?: string;
    mutedText?: string;
    inputChrome?: string;
    surfaceMuted?: string;
    borderMuted?: string;
    celebration?: CelebrationPalette;
    errorLight?: string;
    warningLight?: string;
    successLight?: string;
    infoLight?: string;
  }
  interface TypographyVariants {
    display: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    display?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display: true;
  }
}

// DESIGN.md → Buttons → "Neutral secondary — surface-muted filled". A neutral
// filled secondary for use beside a cinnabar contained primary (avoids "two
// reds"). Registered only on `studentTheme` (light) — it has no dark equivalent.
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    neutral: true;
  }
}

export const shade = (color: string, p: number) =>
  `${color}${Math.round((p / 100) * 255)
    .toString(16)
    .padStart(2, "0")}`;
export const getColor = (mode: PaletteMode) =>
  mode === "light" ? darken : lighten;
export const getBGColor = (mode: PaletteMode) =>
  mode === "light" ? lighten : darken;

// Shared theme config used by both `studentTheme` and `adminTheme`. PR 3 of the
// design system migration introduces `AdminThemeLayout` (in
// `src/themeLayouts.tsx`) as a layout-route override so that PR 4 can flip only
// the student palette to cinnabar; until then both themes resolve to the same
// values and there should be zero visual change.
//
// default values: https://mui.com/material-ui/customization/default-theme
const baseThemeOptions: ThemeOptions = {
  shadows: NO_SHADOWS,
  palette: {
    primary: {
      main: "#4B75B6",
      dark: "#002D5A",
      light: "#DCE9FF",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#F9A300",
      dark: "#825400",
      light: "#FFE9C1",
    },
    success: {
      main: "#059268",
      dark: "#004928",
      light: "#CAF2E6",
    },
    // OLD
    secondary: {
      main: "#F24029",
      dark: "#B5170E",
      light: "#FFE6EA",
    },
    info: {
      main: "#2E47BD",
      dark: "#001D92",
      light: "#E6E9F7",
    },
    error: {
      main: "#DE3535",
      dark: "#920000",
      light: "#E55D5D",
    },
    text: {
      primary: "#1E1B16",
      secondary: "rgba(0 0 0 / .6)",
    },
    // DESIGN.md tokens — additive (PR 1 of design system migration).
    // Existing palette values above are unchanged this phase.
    cinnabar: designTokens.cinnabar,
    brown: designTokens.brown,
    nearBlack: designTokens.nearBlack,
    momentum: designTokens.momentum,
    momentumMuted: designTokens.momentumMuted,
    amber: designTokens.amber,
    inactiveGray: designTokens.inactiveGray,
    borderToken: designTokens.borderToken,
    mutedText: designTokens.mutedText,
    inputChrome: designTokens.inputChrome,
    surfaceMuted: designTokens.surfaceMuted,
    borderMuted: designTokens.borderMuted,
    celebration: celebrationTokens,
    errorLight: designTokens.errorLight,
    warningLight: designTokens.warningLight,
    successLight: designTokens.successLight,
    infoLight: designTokens.infoLight,
    // The following colors are the MUI defaults:
    // background
    // action
    // text
    // other
    // common
  },
  typography: {
    fontFamily:
      "'PP Neue Montreal', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    // DESIGN.md "display" variant — 48px PP Fragment (Glare Light 300) for hero
    // headlines. PP Fragment ships only weight 300.
    display: {
      fontFamily: "'PP Fragment', sans-serif",
      fontWeight: 300,
      fontSize: "48px",
      lineHeight: 1.1,
    },
    h1: {
      fontFamily: "'PP Fragment', sans-serif",
      fontWeight: 300, // PP Fragment Glare Light — only licensed weight
      fontSize: "36px",
      lineHeight: 1.15,
      letterSpacing: "0px",
    },
    h2: {
      fontFamily: "'PP Fragment', sans-serif",
      fontWeight: 300, // PP Fragment Glare Light — only licensed weight
      fontSize: "28px",
      lineHeight: 1.2,
      letterSpacing: "0px",
    },
    h3: {
      fontWeight: 500, // 'medium'
      fontSize: "24px",
      lineHeight: 1.3,
      letterSpacing: "0px",
    },
    // h4/h5/h6 are not in DESIGN.md — the spec stops at h3. Sized below h3 to
    // preserve heading hierarchy for any legacy `variant="h4|h5|h6"` consumers.
    h4: {
      fontWeight: 500, // 'medium'
      fontSize: "22px",
      lineHeight: 1.35,
      letterSpacing: "0px",
    },
    h5: {
      fontWeight: 500, // 'medium'
      fontSize: "20px",
      lineHeight: 1.4,
      letterSpacing: "0px",
    },
    h6: {
      fontWeight: 500, // 'medium'
      fontSize: "16px",
      lineHeight: 1.5,
      letterSpacing: "0px",
    },
    subtitle1: {
      fontWeight: 500, // 'medium'
      fontSize: "20px",
      lineHeight: "175%",
      letterSpacing: "0px",
    },
    subtitle2: {
      fontWeight: 500, // 'medium'
      fontSize: "16px",
      lineHeight: "157%",
      letterSpacing: "0px",
    },
    body1: {
      fontWeight: 400, // 'regular'
      fontSize: "16px",
      lineHeight: "150%",
      letterSpacing: "0px",
    },
    body2: {
      fontWeight: 400, // 'regular'
      fontSize: "14px",
      lineHeight: "143%",
      letterSpacing: "0.02px",
    },
    caption: {
      fontWeight: 400, // 'regular'
      fontSize: "12px",
      lineHeight: "166%",
      letterSpacing: "0.2px",
    },
    overline: {
      fontWeight: 400, // 'regular'
      fontSize: "12px",
      lineHeight: "266%",
      letterSpacing: "0.6px",
      textTransform: "uppercase",
    },
    button: {
      // This applies if the button `size`="medium"
      fontWeight: 500, // 'medium'
      fontSize: "14px",
      lineHeight: "143%",
      letterSpacing: "0.02px",
    },
    // The following texts are customized through components, below:
    // Button small, medium, large
    // Avatar Initials
    // Input Label
    // Helper Text
    // Input Text
    // Tooltip
  },
  components: {
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          // Was Space Grotesk bold; PP Fragment ships only weight 300, so this
          // title maps to PP Neue Montreal Medium (the h3/card-title tier) to
          // keep emphasis without faux-bolding a light-only display face.
          fontFamily: "'PP Neue Montreal', sans-serif",
          fontWeight: 500,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "4px",
        },
        // DESIGN.md → Buttons → States: outlined hover is an 8% fill of the
        // button's own color (MUI's default is ~4% — an ambiguous gray). One
        // ownerState-aware rule covers every color variant; for `primary` this
        // equals `palette.selectionWash`, matching the selected/active fills.
        outlined: ({ theme, ownerState }) => {
          if (!ownerState.color || ownerState.color === "inherit") return {};
          const main = theme.palette[ownerState.color as "primary"]?.main;
          return main ? { "&:hover": { backgroundColor: wash(main) } } : {};
        },
        sizeLarge: {
          fontWeight: 500, // 'medium'
          fontSize: "15px",
          lineHeight: "26px",
          letterSpacing: "0.2px",
          paddingBottom: "8px",
          paddingTop: "8px",
          paddingLeft: "22px",
          paddingRight: "22px",
        },
        sizeMedium: {
          fontWeight: 500, // 'medium'
          fontSize: "14px",
          lineHeight: "24px",
          letterSpacing: "0.2px",
          paddingBottom: "6px",
          paddingTop: "6px",
          paddingLeft: "16px",
          paddingRight: "16px",
        },
        sizeSmall: {
          fontWeight: 500, // 'medium'
          fontSize: "13px",
          lineHeight: "22px",
          letterSpacing: "0.2px",
          paddingBottom: "6px",
          paddingTop: "6px",
          paddingLeft: "16px",
          paddingRight: "16px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          // See MuiAlertTitle: card titles map to PP Neue Montreal Medium (h3
          // tier), not the 300-only PP Fragment display face.
          fontFamily: "'PP Neue Montreal', sans-serif",
          fontWeight: 500,
          // 20px (below DESIGN.md's 24px h3): a card *section* title should sit
          // under the page title, and 24px read oversized on both mobile and
          // desktop once the PP Neue Montreal metrics landed.
          fontSize: 20,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: "100%",
        },
        body: {
          backgroundColor: "#FFFFFF",
          height: "100%",
        },
        "#root": {
          height: "100%",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          // See MuiAlertTitle: dialog titles map to PP Neue Montreal Medium (h3
          // tier), not the 300-only PP Fragment display face.
          fontFamily: "'PP Neue Montreal', sans-serif",
          fontWeight: 500,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderWidth: "0.5px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.highlight": {
            backgroundColor: "#F9C3AC",
            transition: "background-color 1s",
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "&.highlight": {
            backgroundColor: "#F9C3AC",
            transition: "background-color 1s",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
        icon: {
          alignItems: "center",
        },
        standardInfo: ({ theme: currentTheme }) => ({
          color: getColor(currentTheme.palette.mode)(
            currentTheme.palette.info.main,
            0.6
          ),
          background: getBGColor(currentTheme.palette.mode)(
            currentTheme.palette.info.main,
            0.9
          ),
          "& .MuiAlert-icon": {
            color: currentTheme.palette.info.main,
          },
        }),
      },
    },
  },
};

// PR 4 of the design system migration — `studentTheme` swaps to the
// cinnabar/spec palette; `adminTheme` stays on the legacy blue palette
// (unchanged from `baseThemeOptions`). The `AdminThemeLayout` route-group
// scaffold from PR 3 isolates the visual change to non-admin routes.
//
// We have NO second brand color — DESIGN.md's only action color is cinnabar
// ("Secondary CTA = cinnabar outline"). But MUI's palette always carries a
// `secondary` slot: omit it and `createTheme` fills it with the default purple
// (#9c27b0). So `secondary` is aliased to the SAME cinnabar as `primary` — a
// defensive backstop, not a distinct hue. This
// guarantees any lingering or future `secondary.main/.dark/.light` reference
// (selected borders, voice-flow accents, progress) renders on-brand instead of
// purple or the legacy `#F24029`. MUI auto-computes `.dark`/`.light` for hover.
// Button `color="secondary"` props were separately migrated to `primary` for
// explicit intent; `adminTheme` keeps the legacy secondary from
// `baseThemeOptions`.
//
// `primary.dark` is intentionally NOT overridden — MUI auto-computes it (a
// darker cinnabar) and uses it for button hover/active states, which is
// correct. Legacy surfaces that used `primary.dark` as a navy background fill
// (Header, Menu, the quiz intro/grades posters) were migrated to
// `palette.brown` so they don't render as the auto-computed maroon.
export const studentTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    ...baseThemeOptions.palette,
    primary: {
      main: designTokens.cinnabar,
      contrastText: "#FFFFFF",
    },
    // Alias, not a 2nd brand color — see note above. MUI mandates a `secondary`
    // slot (defaults to purple if omitted); point it at cinnabar as a backstop.
    secondary: {
      main: designTokens.cinnabar,
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#B91C1C",
      light: designTokens.errorLight,
    },
    warning: {
      main: "#D97706",
      light: designTokens.warningLight,
    },
    success: {
      main: designTokens.momentum,
      light: designTokens.successLight,
    },
    info: {
      main: "#1D4ED8",
      light: designTokens.infoLight,
    },
    text: {
      primary: designTokens.nearBlack,
      secondary: designTokens.mutedText,
    },
    divider: designTokens.borderToken,
  },
  // Override MUI's default of using `palette.primary.main` for input focus +
  // label colors. Cinnabar there reads as an error state, so use `inputChrome`
  // (warm dark gray) instead — see DESIGN.md → Inputs. The focus + label
  // overrides are scoped with `:not(.Mui-error)` so an input that is focused
  // AND in error still renders the error color (red border, red label), per
  // DESIGN.md → Inputs error-state spec; without the exclusion our focus
  // override would mask MUI's error styling.
  components: {
    ...baseThemeOptions.components,
    // Student card titles use the PP Fragment display serif (weight 300) —
    // "headers serif, everything else PP Neue Montreal." This overrides the
    // sans `MuiCardHeader.title` from baseThemeOptions, which `adminTheme`
    // keeps (institutional register). See DESIGN.md → Cards.
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontFamily: "'PP Fragment', sans-serif",
          fontWeight: 300,
          fontSize: 20,
        },
      },
    },
    // DESIGN.md → Buttons → "Neutral secondary — surface-muted filled".
    // Student/light only: surface-muted (#FAF8F8) fill + border-muted (#E8E0DF)
    // border + near-black label. Spread the base MuiButton so the shared
    // root/outlined/size overrides are preserved; add the `neutral` variant.
    MuiButton: {
      ...baseThemeOptions.components?.MuiButton,
      variants: [
        {
          props: { variant: "neutral" },
          style: {
            backgroundColor: designTokens.surfaceMuted,
            border: `1px solid ${designTokens.borderMuted}`,
            color: designTokens.nearBlack,
            // Affordance rides on the border — keep it on hover too.
            "&:hover": {
              backgroundColor: darken(designTokens.surfaceMuted, 0.04),
              border: `1px solid ${designTokens.borderMuted}`,
            },
            "&.Mui-disabled": {
              opacity: 0.4,
              color: designTokens.nearBlack,
            },
          },
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: designTokens.inputChrome,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: designTokens.inputChrome,
          "&.Mui-focused:not(.Mui-error)": {
            color: designTokens.inputChrome,
          },
        },
      },
    },
  },
});
export const adminTheme = createTheme(baseThemeOptions);

// DESIGN.md "Government & Admin Theme (Dark)" — near-black surface, white text,
// white-alpha borders. Scoped (via a ThemeProvider in AdminRoutes) to the
// Success Queue, the one admin surface designed dark. Built from
// `baseThemeOptions` so it keeps the shared typography, no-shadows, button
// radius, etc.; only the palette flips to the dark spec. Reads the same
// `designTokens` (cinnabar/amber/momentum) every other consumer uses.
// The dark palette MUST live in the FIRST `createTheme` arg: MUI computes
// derived values (`action.disabled/hover/selected`, contrast text) from
// `palette.mode` when it builds the theme. Passing `{ mode: "dark" }` only as a
// second (deep-merged) arg leaves `action.*` at the light-mode black-alpha
// defaults — which renders disabled buttons black-on-near-black (invisible). So
// the full palette goes in arg 1; only the `components` override (which isn't
// recomputed) goes in arg 2, where it deep-merges onto the base MuiPaper rules.
export const successQueueTheme = createTheme(
  {
    ...baseThemeOptions,
    palette: {
      ...baseThemeOptions.palette,
      mode: "dark",
      // Filled buttons / active indicators are white on near-black (DESIGN.md:
      // government CTAs are white, not cinnabar).
      primary: {
        main: "#FFFFFF",
        dark: "#E5E7EB",
        light: "#FFFFFF",
        contrastText: designTokens.nearBlack,
      },
      background: {
        default: designTokens.nearBlack,
        paper: designTokens.nearBlack,
      },
      // Alphas are set by measured contrast against nearBlack (#110A09), not by
      // eye. At 0.6, secondary is 7.29:1 and already passes. The two below were
      // measured failures:
      //   disabled 0.40 -> 3.78:1, under the 4.5:1 AA floor for body text
      //   divider  0.20 -> 1.78:1, under the 3:1 floor for UI boundaries
      // 0.50 and 0.36 clear both with margin (5.30:1 and 3.24:1).
      text: {
        primary: "#FFFFFF",
        secondary: "rgba(255, 255, 255, 0.6)",
        disabled: "rgba(255, 255, 255, 0.5)",
      },
      divider: "rgba(255, 255, 255, 0.36)",
      // Semantic colors used as single-signal accents (status dots, the SLA
      // callout border, queued-delivery markers) — DESIGN.md semantic palette.
      error: { main: "#B91C1C", dark: "#7F1515", light: "#F87171" },
      warning: { main: designTokens.amber, dark: "#B45309", light: "#FBBF24" },
      success: {
        main: designTokens.momentum,
        dark: "#14532D",
        light: "#34D399",
      },
      info: { main: "#1D4ED8", dark: "#1E3A8A", light: "#60A5FA" },
    },
  },
  {
    components: {
      // MUI's dark mode paints an elevation "lightening" gradient on every Paper
      // (Dialog, Menu, Popover) via backgroundImage. DESIGN.md wants flat
      // near-black surfaces with borders for separation — kill the overlay so
      // menus/dialogs render the same #110A09 as the columns.
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
    },
  }
);

// `selectionWash` can't live in a palette literal (a literal can't reference
// its own `primary.main`), so derive it post-creation: an 8% wash of each
// theme's resolved primary. Keeps it provably in sync with the brand color
// with no duplicated hexes.
studentTheme.palette.selectionWash = wash(studentTheme.palette.primary.main);
adminTheme.palette.selectionWash = wash(adminTheme.palette.primary.main);
successQueueTheme.palette.selectionWash = wash(
  successQueueTheme.palette.primary.main
);

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
});
